import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Star, ArrowRight } from "lucide-react";
import { useLang } from "@/lib/lang";
import { HERO_SLIDES } from "./constants";

/**
 * Hero section with rotating full-bleed background slides, title, subtitle and CTAs.
 */
export default function Hero({ onBook, onScrollTo }) {
  const [slide, setSlide] = useState(0);
  const reduce = useReducedMotion();
  const { t } = useLang();

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="top" className="dt-hero" data-testid="hero-section">
      <div className="dt-hero__slides">
        {HERO_SLIDES.map((s, i) => (
          <div
            key={s.src}
            className={`dt-hero__slide ${i === slide ? "is-active" : ""}`}
            style={{ backgroundImage: `url(${s.src})` }}
            aria-label={s.alt}
          />
        ))}
      </div>
      <div className="dt-hero__overlay" />
      <div className="dt-hero__inner">
        <motion.span
          className="dt-hero__badge"
          initial={reduce ? false : { opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Star size={14} fill="currentColor" /> {t("landing_hero_badge")}
        </motion.span>
        <motion.h1
          className="dt-hero__title"
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          {t("landing_hero_title_pre")} <span className="accent">{t("landing_hero_title_accent")}</span>
        </motion.h1>
        <motion.p
          className="dt-hero__subtitle"
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
        >
          {t("landing_hero_subtitle")}
        </motion.p>
        <motion.div
          className="dt-hero__ctas"
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <button
            type="button"
            className="dt-btn dt-btn--primary"
            onClick={() => onBook("executive")}
            data-testid="hero-cta-book"
          >
            {t("landing_hero_cta_book")} <ArrowRight size={16} />
          </button>
          <button
            type="button"
            className="dt-btn dt-btn--ghost"
            onClick={() => onScrollTo("tours")}
            data-testid="hero-cta-tours"
          >
            {t("landing_hero_cta_tours")}
          </button>
        </motion.div>
        <div className="dt-hero__dots" data-testid="hero-dots">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              className={`dt-hero__dot ${i === slide ? "is-active" : ""}`}
              onClick={() => setSlide(i)}
              aria-label={`Slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      </div>
      <div className="dt-hero__scroll" aria-hidden>
        <span />
      </div>
    </section>
  );
}
